import { useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Memoise the settings call so we make exactly one request per page-load
// instead of one per route. Refreshed whenever the user reloads the tab.
let settingsPromise = null;
function loadSettings() {
  if (!settingsPromise) {
    settingsPromise = axios.get(`${API}/public/settings`).then((r) => r.data || {}).catch(() => ({}));
  }
  return settingsPromise;
}

function absolutise(url, base) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${(base || BACKEND || "").replace(/\/+$/, "")}${url.startsWith("/") ? "" : "/"}${url}`;
}

const MANAGED_ATTR = "data-page-meta";

/**
 * Replace (or create) a <meta>/<link> tag and tag it as managed by this hook
 * so we can clean up cleanly on unmount.
 */
function setMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(selector.startsWith("link") ? "link" : "meta");
    // Apply identifying attributes from selector
    const id = /\[([^=]+)="([^"]+)"\]/g;
    let m;
    while ((m = id.exec(selector)) !== null) {
      el.setAttribute(m[1], m[2]);
    }
    document.head.appendChild(el);
  }
  for (const [k, v] of Object.entries(attrs)) {
    if (v == null) continue;
    el.setAttribute(k, v);
  }
  el.setAttribute(MANAGED_ATTR, "1");
}

function clearManaged() {
  document.head.querySelectorAll(`[${MANAGED_ATTR}]`).forEach((n) => n.remove());
}

/**
 * usePageMeta — sets title + meta description + canonical + Open Graph + Twitter Card.
 *
 * @param {object}  opts
 * @param {string?} opts.title          page <title>
 * @param {string?} opts.description    meta description
 * @param {string?} opts.canonical      absolute URL override; default = site_base_url + window.location.pathname
 * @param {string?} opts.ogImage        absolute or /api/uploads/... — default uses settings.default_og_image
 * @param {string?} opts.ogType         "website" (default), "article", "product"
 * @param {string?} opts.keywords       comma-separated string
 * @param {string?} opts.robots         e.g. "index,follow" or "noindex,follow"
 */
export default function usePageMeta(opts = {}) {
  const { title, description, canonical, ogImage, ogType, keywords, robots } = opts;

  useEffect(() => {
    if (!title && !description && !canonical && !ogImage) return;
    const prevTitle = document.title;
    let cancelled = false;

    loadSettings().then((s) => {
      if (cancelled) return;
      const baseUrl = (s.site_base_url || window.location.origin || "").replace(/\/+$/, "");
      const path = window.location.pathname + window.location.search;
      const canonicalHref = canonical || `${baseUrl}${path}`;
      const ogImg = absolutise(ogImage || s.default_og_image || s.logo_url, baseUrl) || null;
      const siteName = s.og_site_name || s.site_title || "Bon-Heur Pharmaceuticals";
      const twitter = s.twitter_handle || null;
      const twitterCard = s.twitter_card_type || "summary_large_image";
      const finalTitle = title || s.site_title || "";
      const finalDesc = description || "";

      if (finalTitle) document.title = finalTitle;

      // Clear previously managed nodes before re-injecting (route change)
      clearManaged();

      // ── Description ──
      if (finalDesc) setMeta('meta[name="description"]', { name: "description", content: finalDesc });

      // ── Keywords (optional) ──
      if (keywords) setMeta('meta[name="keywords"]', { name: "keywords", content: keywords });

      // ── Robots ──
      if (robots) setMeta('meta[name="robots"]', { name: "robots", content: robots });

      // ── Canonical ──
      setMeta('link[rel="canonical"]', { rel: "canonical", href: canonicalHref });

      // ── Open Graph ──
      setMeta('meta[property="og:type"]',        { property: "og:type", content: ogType || "website" });
      setMeta('meta[property="og:title"]',       { property: "og:title", content: finalTitle });
      if (finalDesc) setMeta('meta[property="og:description"]', { property: "og:description", content: finalDesc });
      setMeta('meta[property="og:url"]',         { property: "og:url", content: canonicalHref });
      setMeta('meta[property="og:site_name"]',   { property: "og:site_name", content: siteName });
      if (ogImg) setMeta('meta[property="og:image"]', { property: "og:image", content: ogImg });

      // ── Twitter Card ──
      setMeta('meta[name="twitter:card"]',        { name: "twitter:card", content: twitterCard });
      if (twitter) setMeta('meta[name="twitter:site"]', { name: "twitter:site", content: twitter });
      setMeta('meta[name="twitter:title"]',       { name: "twitter:title", content: finalTitle });
      if (finalDesc) setMeta('meta[name="twitter:description"]', { name: "twitter:description", content: finalDesc });
      if (ogImg)     setMeta('meta[name="twitter:image"]',       { name: "twitter:image", content: ogImg });
    });

    return () => {
      cancelled = true;
      // On route change we'll re-inject; restore the title to whatever was there
      // before this page mounted so admin pages keep their global brand title.
      if (title) document.title = prevTitle;
      clearManaged();
    };
  }, [title, description, canonical, ogImage, ogType, keywords, robots]);
}
